const DESKTOP_QUERY = '(min-width: 750px)';
const PRODUCT_DATA_CACHE = new Map();
const SIZE_GUIDE_CACHE = new Map();
const QUICK_BUY_STATE = new WeakMap();
const modalContent = document.getElementById('quick-add-modal-content');
const quickAddDialog = document.getElementById('quick-add-dialog');
let activeSizeGuide = null;

function optimizedImageUrl(source, width) {
  if (!source) return '';

  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return source;
  }
}

function normalizeImageUrl(source) {
  if (!source) return '';

  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.delete('width');
    return `${url.hostname}${url.pathname}`;
  } catch {
    return source.split('?')[0];
  }
}

function productUrlFromRoot(root) {
  return root.querySelector('.view-product-title a')?.href || '';
}

function selectedOptions(root) {
  return Object.fromEntries(
    [...root.querySelectorAll('variant-picker input[type="radio"]:checked')]
      .filter((input) => input.dataset.optionName)
      .map((input) => [input.dataset.optionName, input.value])
  );
}

function optionNames(productData) {
  return (productData.options || []).map((option) => (typeof option === 'string' ? option : option.name));
}

function selectedVariant(root, productData) {
  const selected = selectedOptions(root);
  const names = optionNames(productData);

  return (productData.variants || []).find((variant) =>
    names.every((name, index) => !selected[name] || variant.options?.[index] === selected[name])
  );
}

function variantImage(variant) {
  return variant?.featured_media?.preview_image || variant?.featured_image || null;
}

function productMedia(productData) {
  return (productData.media || [])
    .filter((media) => media.media_type === 'image')
    .map((media) => ({
      id: String(media.id),
      src: media.src || media.preview_image?.src,
      width: media.width || media.preview_image?.width,
      height: media.height || media.preview_image?.height,
    }))
    .filter((media) => media.src);
}

async function fetchProductData(productUrl) {
  const url = new URL(productUrl, window.location.origin);
  const cacheKey = url.pathname;

  if (!PRODUCT_DATA_CACHE.has(cacheKey)) {
    PRODUCT_DATA_CACHE.set(
      cacheKey,
      fetch(`${url.pathname}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => {
        if (!response.ok) throw new Error(`Quick buy product request failed: ${response.status}`);
        return response.json();
      })
    );
  }

  return PRODUCT_DATA_CACHE.get(cacheKey);
}

async function fetchSizeGuide(productUrl) {
  const url = new URL(productUrl, window.location.origin);
  const cacheKey = url.pathname;

  if (!SIZE_GUIDE_CACHE.has(cacheKey)) {
    SIZE_GUIDE_CACHE.set(
      cacheKey,
      fetch(url.pathname, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Size guide request failed: ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = documentFragment.querySelector('.gksc-modal');
        const content = source?.querySelector('.gksc-modal__content');

        if (!source || !content) throw new Error('This product does not have a size guide');

        return {
          title: source.querySelector('.gksc-modal__title')?.textContent?.trim() || 'Size guide',
          unit: source.querySelector('.gksc-modal__unit')?.textContent?.trim() || '',
          content: content.innerHTML,
        };
      })
    );
  }

  return SIZE_GUIDE_CACHE.get(cacheKey);
}

function closeSizeGuide() {
  if (!activeSizeGuide) return;

  const { modal, trigger } = activeSizeGuide;
  modal.classList.remove('is-open');
  window.setTimeout(() => {
    modal.remove();
    trigger?.focus();
  }, 180);
  activeSizeGuide = null;
}

async function openSizeGuide(trigger, productUrl) {
  if (!productUrl || trigger.dataset.loading === 'true') return;

  trigger.dataset.loading = 'true';
  trigger.setAttribute('aria-busy', 'true');

  try {
    const guide = await fetchSizeGuide(productUrl);
    closeSizeGuide();

    const modal = document.createElement('div');
    modal.className = 'gk-quick-buy-chart';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', guide.title);
    modal.innerHTML = `
      <button type="button" class="gk-quick-buy-chart__backdrop" aria-label="Close size guide"></button>
      <div class="gk-quick-buy-chart__panel">
        <header class="gk-quick-buy-chart__header">
          <div>
            <h2 class="gk-quick-buy-chart__title"></h2>
            <p class="gk-quick-buy-chart__unit"></p>
          </div>
          <button type="button" class="gk-quick-buy-chart__close" aria-label="Close size guide">×</button>
        </header>
        <div class="gk-quick-buy-chart__body">
          <div class="gk-quick-buy-chart__content"></div>
        </div>
      </div>
    `;

    modal.querySelector('.gk-quick-buy-chart__title').textContent = guide.title;
    const unit = modal.querySelector('.gk-quick-buy-chart__unit');
    unit.textContent = guide.unit;
    unit.hidden = !guide.unit;
    modal.querySelector('.gk-quick-buy-chart__content').innerHTML = guide.content;

    const dialog = quickAddDialog?.querySelector('dialog') || modalContent;
    dialog?.append(modal);
    activeSizeGuide = { modal, trigger };
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      modal.querySelector('.gk-quick-buy-chart__close')?.focus();
    });
  } catch (error) {
    console.warn('[GKIKZ quick buy] Size guide unavailable', error);
    trigger.textContent = 'Size guide unavailable';
    window.setTimeout(() => {
      trigger.textContent = 'Size guide';
    }, 1800);
  } finally {
    trigger.dataset.loading = 'false';
    trigger.removeAttribute('aria-busy');
  }
}

function renderMedia(state, media, title) {
  if (!media?.src) return;

  state.index = Math.max(
    0,
    state.media.findIndex(
      (candidate) =>
        (media.id && String(candidate.id) === String(media.id)) ||
        normalizeImageUrl(candidate.src) === normalizeImageUrl(media.src)
    )
  );

  state.image.classList.add('is-changing');
  state.image.src = optimizedImageUrl(media.src, 900);
  state.image.alt = title;
  state.image.width = media.width || 900;
  state.image.height = media.height || 1200;
  state.shell.dataset.mediaIndex = String(state.index);
  requestAnimationFrame(() => state.image.classList.remove('is-changing'));
}

function stepMedia(state, direction) {
  if (state.media.length < 2) return;
  state.shell.dataset.lastDirection = String(direction);
  state.index = (state.index + direction + state.media.length) % state.media.length;
  renderMedia(state, state.media[state.index], state.title);
}

function buildMediaShell(root, productData) {
  let shell = root.querySelector('.gk-quick-buy-media');
  const originalMedia = root.querySelector('.product-information__media');

  if (!shell) {
    shell = document.createElement('div');
    shell.className = 'gk-quick-buy-media';
    shell.innerHTML = `
      <img class="gk-quick-buy-media__image" loading="eager" decoding="async">
      <div class="gk-quick-buy-media__controls">
        <button type="button" class="gk-quick-buy-media__arrow" data-direction="-1" aria-label="Previous product image">←</button>
        <button type="button" class="gk-quick-buy-media__arrow" data-direction="1" aria-label="Next product image">→</button>
      </div>
    `;

    if (originalMedia) {
      originalMedia.replaceWith(shell);
    } else {
      root.prepend(shell);
    }
  } else {
    originalMedia?.remove();
  }

  const state = {
    shell,
    image: shell.querySelector('.gk-quick-buy-media__image'),
    media: productMedia(productData),
    index: 0,
    title: productData.title || '',
    productData,
    variantId: null,
  };

  shell.classList.toggle('has-multiple-images', state.media.length > 1);
  QUICK_BUY_STATE.set(root, state);
  return state;
}

function decorateColorOptions(root, productData) {
  const names = optionNames(productData);
  const colorIndex = names.findIndex((name) => /colou?r/i.test(name));
  if (colorIndex < 0) return;

  const colorInputs = root.querySelectorAll(
    `variant-picker input[type="radio"][data-option-name="${CSS.escape(names[colorIndex])}"]`
  );
  const fieldset = colorInputs[0]?.closest('fieldset');
  fieldset?.classList.add('gk-quick-buy-color-options');

  colorInputs.forEach((input) => {
    const variant =
      productData.variants.find(
        (candidate) => candidate.options?.[colorIndex] === input.value && candidate.available
      ) || productData.variants.find((candidate) => candidate.options?.[colorIndex] === input.value);
    const image = variantImage(variant);
    const label = input.closest('label');

    if (!label || !image?.src) return;
    label.classList.add('gk-quick-buy-color-swatch');
    label.style.setProperty('--gk-quick-buy-swatch-image', `url("${optimizedImageUrl(image.src, 180)}")`);
    label.title = input.value;
  });
}

function addProductLinks(root, productUrl, variant) {
  const sizeInput = root.querySelector('variant-picker input[data-option-name="Size"]');
  const sizeFieldset = sizeInput?.closest('fieldset');

  if (sizeFieldset && !sizeFieldset.querySelector('.gk-quick-buy-size-guide')) {
    const sizeGuide = document.createElement('button');
    sizeGuide.type = 'button';
    sizeGuide.className = 'gk-quick-buy-size-guide';
    sizeGuide.textContent = 'Size guide';
    sizeGuide.title = 'Open the product size guide';
    sizeGuide.setAttribute('aria-haspopup', 'dialog');
    sizeFieldset.append(sizeGuide);
  }

  let detailsLink = root.querySelector('.gk-quick-buy-full-details');
  if (!detailsLink) {
    detailsLink = document.createElement('a');
    detailsLink.className = 'gk-quick-buy-full-details';
    detailsLink.textContent = 'View full product details';
    (root.querySelector('.buy-buttons-block') || root.querySelector('.product-details'))?.append(detailsLink);
  }

  const url = new URL(productUrl, window.location.origin);
  if (variant?.id) url.searchParams.set('variant', String(variant.id));
  if (sizeFieldset) sizeFieldset.querySelector('.gk-quick-buy-size-guide').dataset.productUrl = url.toString();
  if (detailsLink) detailsLink.href = url.toString();
}

function syncButtonPrice(root, variant, productData) {
  const button = root.querySelector('button[name="add"]');
  if (!button) return;

  let price = root.querySelector('product-price .price:not(.compare-at-price) .money')?.textContent?.trim();
  if (!price && variant?.price != null) {
    const currency = window.Shopify?.currency?.active || 'USD';
    price = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(variant.price / 100);
  }

  let priceElement = button.querySelector('.gk-quick-buy-button-price');
  if (!priceElement) {
    priceElement = document.createElement('span');
    priceElement.className = 'gk-quick-buy-button-price';
    button.append(priceElement);
  }
  priceElement.textContent = price || '';
  button.setAttribute('aria-label', `Add ${productData.title || 'product'} to cart${price ? ` for ${price}` : ''}`);
}

function syncQuickBuy(root, productData, productUrl) {
  if (!root.isConnected || !window.matchMedia(DESKTOP_QUERY).matches) return;

  const state = QUICK_BUY_STATE.get(root) || buildMediaShell(root, productData);
  const variant = selectedVariant(root, productData);
  const image = variantImage(variant) || state.media[0];
  const variantId = variant?.id ? String(variant.id) : '';

  state.productData = productData;
  if (state.variantId !== variantId || !state.image.src) {
    renderMedia(state, image, productData.title || '');
    state.variantId = variantId;
  }
  decorateColorOptions(root, productData);
  addProductLinks(root, productUrl, variant);
  syncButtonPrice(root, variant, productData);
}

async function enhanceDesktopQuickBuy() {
  if (!modalContent || !window.matchMedia(DESKTOP_QUERY).matches || !modalContent.children.length) return;

  const productUrl = productUrlFromRoot(modalContent);
  if (!productUrl) return;

  modalContent.classList.add('gk-quick-buy-enhanced');
  quickAddDialog?.querySelector('dialog')?.classList.add('gk-quick-buy-ready');

  const originalMedia = modalContent.querySelector('.product-information__media');
  if (originalMedia && !modalContent.querySelector('.gk-quick-buy-media')) {
    const firstImage = originalMedia.querySelector('img');
    const placeholder = document.createElement('div');
    placeholder.className = 'gk-quick-buy-media';
    placeholder.innerHTML = `
      <img class="gk-quick-buy-media__image" loading="eager" decoding="async">
      <div class="gk-quick-buy-media__controls">
        <button type="button" class="gk-quick-buy-media__arrow" data-direction="-1" aria-label="Previous product image">←</button>
        <button type="button" class="gk-quick-buy-media__arrow" data-direction="1" aria-label="Next product image">→</button>
      </div>
    `;
    const placeholderImage = placeholder.querySelector('img');
    placeholderImage.src = optimizedImageUrl(firstImage?.getAttribute('src') || '', 900);
    placeholderImage.alt = firstImage?.alt || '';
    originalMedia.replaceWith(placeholder);
  }

  try {
    const productData = await fetchProductData(productUrl);
    syncQuickBuy(modalContent, productData, productUrl);
  } catch (error) {
    console.warn('[GKIKZ quick buy] Product data unavailable', error);
  }
}

function decorateQuickBuyTriggers() {
  document.querySelectorAll('.quick-add__button--choose').forEach((button) => {
    const title = button.closest('quick-add-component')?.dataset.productTitle || 'product';
    button.setAttribute('aria-label', `Quick buy ${title}`);
    button.title = `Quick buy ${title}`;
  });
}

let enhancementFrame;
function scheduleEnhancement() {
  cancelAnimationFrame(enhancementFrame);
  enhancementFrame = requestAnimationFrame(() => {
    decorateQuickBuyTriggers();
    enhanceDesktopQuickBuy();
  });
}

modalContent?.addEventListener('change', (event) => {
  if (!window.matchMedia(DESKTOP_QUERY).matches || !(event.target instanceof HTMLInputElement)) return;
  const state = QUICK_BUY_STATE.get(modalContent);
  const productUrl = productUrlFromRoot(modalContent);
  if (!state || !productUrl) return;

  requestAnimationFrame(() => syncQuickBuy(modalContent, state.productData, productUrl));
  window.setTimeout(() => syncQuickBuy(modalContent, state.productData, productUrl), 500);
});

modalContent?.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const sizeGuide = target?.closest('.gk-quick-buy-size-guide');

  if (sizeGuide instanceof HTMLButtonElement) {
    event.preventDefault();
    openSizeGuide(sizeGuide, sizeGuide.dataset.productUrl);
    return;
  }

  const button = target?.closest('.gk-quick-buy-media__arrow');
  if (!(button instanceof HTMLButtonElement)) return;
  const state = QUICK_BUY_STATE.get(modalContent);
  if (!state) return;
  stepMedia(state, Number(button.dataset.direction));
});

quickAddDialog?.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest('.gk-quick-buy-chart__close, .gk-quick-buy-chart__backdrop')) {
    event.preventDefault();
    closeSizeGuide();
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && activeSizeGuide) {
    event.stopPropagation();
    closeSizeGuide();
  }
});

if (modalContent) {
  new MutationObserver(scheduleEnhancement).observe(modalContent, {
    childList: true,
    subtree: true,
  });
}

window.matchMedia(DESKTOP_QUERY).addEventListener('change', scheduleEnhancement);
document.addEventListener('shopify:section:load', decorateQuickBuyTriggers);
scheduleEnhancement();
